import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import Navbar from "@/components/Navbar";
import PromptBlock from "@/components/PromptBlock";
import NotesPanel from "@/components/NotesPanel";
import AdSlot from "@/components/AdSlot";
import { Check, ArrowLeft, ArrowRight, Bookmark, AlertTriangle } from "lucide-react";

export default function Lesson() {
  const { lessonId } = useParams();
  const { t, lang } = useI18n();
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  const load = useCallback(async () => {
    setLesson(null);
    const { data } = await api.get(`/lessons/${lessonId}?lang=${lang}`);
    setLesson(data);
    api.post(`/progress/${lessonId}/visit`).catch(() => {});
    const fresh = await refreshUser();
    setCompleted((fresh?.completed_lessons || user?.completed_lessons || []).includes(lessonId));
    setBookmarked((fresh?.bookmarks || user?.bookmarks || []).includes(lessonId));
    window.scrollTo(0, 0);
  }, [lessonId, lang]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const markComplete = async () => {
    await api.post(`/progress/${lessonId}/complete`);
    setCompleted(true);
    await refreshUser();
    toast.success(t("lesson.completed"));
  };

  const toggleBookmark = async () => {
    const { data } = await api.post(`/bookmarks/${lessonId}`);
    setBookmarked(data.bookmarked);
    await refreshUser();
  };

  if (!lesson) {
    return (
      <div className="min-h-screen bg-void">
        <Navbar />
        <div className="pt-40 text-center text-ink-soft" data-testid="lesson-loading">{t("common.loading")}</div>
      </div>
    );
  }

  const c = lesson.content;

  return (
    <div className="min-h-screen bg-void">
      <Navbar />
      <main className="pt-28 pb-24 max-w-2xl mx-auto px-6" data-testid="lesson-page">
        <Link to="/course" className="text-gold text-xs tracking-[0.3em] uppercase hover:text-gold-hover transition-colors">
          {lesson.module_title}
        </Link>
        <div className="flex items-start justify-between gap-4 mt-3 mb-8">
          <h1 className="font-serif text-4xl md:text-5xl text-ink leading-tight">{lesson.title}</h1>
          <button onClick={toggleBookmark} data-testid="lesson-bookmark"
            className={`shrink-0 mt-2 transition-colors ${bookmarked ? "text-gold" : "text-ink-muted hover:text-gold"}`}
            title={bookmarked ? t("lesson.bookmarked") : t("lesson.bookmark")}>
            <Bookmark size={22} strokeWidth={1.5} fill={bookmarked ? "#D4AF37" : "none"} />
          </button>
        </div>

        {!lesson.generated && (
          <p className="text-ink-muted text-sm italic mb-8" data-testid="lesson-generating">{t("lesson.generating")}</p>
        )}

        <motion.article initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
          className="prose-editorial space-y-8">
          <p className="text-ink text-xl leading-relaxed">{c.intro}</p>

          {c.sections?.map((s, i) => (
            <div key={i}>
              <h2 className="font-serif text-2xl text-ink mb-3">{s.heading}</h2>
              <p className="text-ink-soft leading-relaxed whitespace-pre-line">{s.body}</p>
            </div>
          ))}

          {c.examples?.length > 0 && (
            <div>
              <h2 className="font-serif text-2xl text-ink mb-3">{t("lesson.examples")}</h2>
              <ul className="space-y-3">
                {c.examples.map((ex, i) => (
                  <li key={i} className="text-ink-soft leading-relaxed pl-4 border-l border-gold/30">{ex}</li>
                ))}
              </ul>
            </div>
          )}

          {c.prompts?.map((p, i) => <PromptBlock key={i} label={p.label} text={p.text} />)}

          {c.exercise && (
            <div className="bg-graphite border border-ink/5 p-6">
              <h2 className="font-serif text-2xl text-ink mb-3">{t("lesson.exercise")}</h2>
              <p className="text-ink-soft leading-relaxed">{c.exercise}</p>
            </div>
          )}

          {c.mistakes?.length > 0 && (
            <div>
              <h2 className="font-serif text-2xl text-ink mb-3 flex items-center gap-2">
                <AlertTriangle size={20} strokeWidth={1.5} className="text-gold-muted" /> {t("lesson.mistakes")}
              </h2>
              <ul className="space-y-2">
                {c.mistakes.map((m, i) => <li key={i} className="text-ink-soft leading-relaxed">— {m}</li>)}
              </ul>
            </div>
          )}

          {c.takeaway && (
            <div className="border-l-2 border-gold pl-6 py-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold-muted mb-2">{t("lesson.takeaway")}</div>
              <p className="font-serif text-2xl text-ink italic leading-snug">{c.takeaway}</p>
            </div>
          )}
        </motion.article>

        <div className="mt-12">
          <button onClick={markComplete} disabled={completed} data-testid="lesson-complete"
            className={`w-full py-3 flex items-center justify-center gap-2 transition-colors ${
              completed ? "border border-gold/40 text-gold" : "bg-gold text-void hover:bg-gold-hover"}`}>
            <Check size={18} strokeWidth={1.5} /> {completed ? t("lesson.completed") : t("lesson.complete")}
          </button>
        </div>

        {/* Dormant ad slot at lesson transition */}
        <AdSlot slot="lesson-transition" className="my-10" />

        <NotesPanel lessonId={lessonId} />

        <nav className="flex justify-between items-center border-t border-ink/5 pt-6 mt-10" data-testid="lesson-nav">
          {lesson.prev ? (
            <button onClick={() => nav(`/lesson/${lesson.prev.lesson_id}`)} data-testid="lesson-prev"
              className="group flex items-center gap-2 text-ink-soft hover:text-gold transition-colors text-left">
              <ArrowLeft size={18} strokeWidth={1.5} />
              <span className="max-w-[9rem] truncate text-sm">{lesson.prev.title}</span>
            </button>
          ) : <span />}
          {lesson.next ? (
            <button onClick={() => nav(`/lesson/${lesson.next.lesson_id}`)} data-testid="lesson-next"
              className="group flex items-center gap-2 text-ink-soft hover:text-gold transition-colors text-right">
              <span className="max-w-[9rem] truncate text-sm">{lesson.next.title}</span>
              <ArrowRight size={18} strokeWidth={1.5} />
            </button>
          ) : <span />}
        </nav>
      </main>
    </div>
  );
}
